import { Mainnet } from "@usedapp/core"
import type { BigNumber } from "ethers"
import { ethers } from "ethers"
import React, { useCallback, useEffect, useState } from "react"
import { botClassicAbi } from "web3/abi/bot-classic"
import { botClassicWrappedAbi } from "web3/abi/bot-classic-wrapped"
import { contractAddresses } from "web3/addresses"

import { useTailwindBreakpoints } from "components/hooks/use-tailwind-breakpoints"
// import { LocalStorageKey, useLocalStorage } from "components/hooks/use-localstorage"
// import { isSandbox } from "config/base"
import { useWeb3Simple } from "components/hooks/web3/use-base"

import { BotCard } from "components/pages/wrap/bot-card"
import { BackgroundImage } from "components/shared/background-img"
import { Box } from "components/shared/box"
import { Rotating } from "components/shared/icons/rotating"
import { Button } from "components/shared/landing/button"
import { Section } from "components/shared/landing/section"
import { Link } from "components/shared/link"
import { Text } from "components/shared/text"

import { retry } from "utils/retry"
import { bnNumberOrZero } from "utils/web3/bn-number-or-zero"
import { cutAddress } from "utils/web3/cut-address"

const links = [
  { label: "OpenSea", url: "https://opensea.io/collection/wrapped-cryptobots" },
  { label: "LooksRare", url: "https://looksrare.org/collections/0xc0BF6533FE18394A581AA38De831C4ecc64cbe55" },
  { label: "x2y2", url: "https://x2y2.io/collection/wrapped-cryptobots/items" },
]

export default function Wrap() {
  const { account, connect, connectWalletConnect, connected, disconnect, loading, library } = useWeb3Simple()

  const [botIds, setBotIds] = useState<number[] | null>(null)
  const [botApproved, setBotApproved] = useState<Record<number, boolean>>({})
  const [wrappedBotsCount, setWrappedBotsCount] = useState(0)
  const [dataLoading, setDataLoading] = useState(false)
  const [botsLoading, setBotsLoading] = useState(false)
  const [botsLoadingError, setBotsLoadingError] = useState(false)

  const [selectedBotIds, setSelectedBotIds] = useState<number[]>([])

  const { isMobile } = useTailwindBreakpoints()

  // const [approvedMap, setApprovedMap] = useLocalStorage(LocalStorageKey.WRAP_CLASSIC_APPROVED_MAP)

  const getWrappedCount = async () => {
    if (!account || !connected || !library || !library.getSigner) {
      return
    }
    setDataLoading(true)
    const signer = library.getSigner()
    const wrappedContract = new ethers.Contract(contractAddresses.BotClassicWrapped, botClassicWrappedAbi, signer)
    const _wrappedBotsCount = bnNumberOrZero(await wrappedContract.balanceOf(account), 0)
    setWrappedBotsCount(_wrappedBotsCount)
    setDataLoading(false)
  }

  const getBotsContract = async () => {
    if (!account || !connected || !library || !library.getSigner) {
      return
    }
    const signer = library.getSigner()
    const classicContract = new ethers.Contract(contractAddresses.BotClassic, botClassicAbi, signer)
    const tokens = await classicContract.tokensOfOwner(account)
    const _botIds: number[] = tokens.map((e: BigNumber) => bnNumberOrZero(e, 0))
    const _botApproved: Record<string, boolean> = {}
    for (let i = 0; i < _botIds.length; i++) {
      const botId = _botIds[i]
      const approvedContract: string = await classicContract.botIndexToApproved(botId)
      const approved = approvedContract.toLowerCase() === contractAddresses.BotClassicWrapped.toLowerCase()
      _botApproved[botId] = approved
    }
    setBotApproved(_botApproved)
    setBotIds(_botIds)
  }

  const getBots = async () => {
    setBotsLoading(true)

    if (!account || !connected || !library || !library.getSigner) {
      return
    }
    setBotsLoadingError(false)
    try {
      await retry(getBotsContract, 5, 300)
    } catch (error) {
      setBotsLoadingError(true)
      console.log("err", error)
    }
    setBotsLoading(false)
  }

  useEffect(() => {
    getWrappedCount()
    setBotApproved({})
    setBotIds(null)
  }, [account])

  const approve = useCallback(
    async (id: number) => {
      if (!connected || !library || !library.getSigner) {
        return
      }
      const signer = library.getSigner()
      const classicContract = new ethers.Contract(contractAddresses.BotClassic, botClassicAbi, signer)
      const approved = (await classicContract.botIndexToApproved(id)) === contractAddresses.BotClassicWrapped

      if (approved) {
        // setApprovedMap({ ...approvedMap, [id]: true })
        return
      }
      const tx = await classicContract.approve(contractAddresses.BotClassicWrapped, id)
      await library.waitForTransaction(tx?.hash)
      // setApprovedMap({ ...approvedMap, [id]: true })
    },
    [connected, account],
  )

  const wrap = useCallback(
    async (id: number) => {
      if (!connected || !library || !library.getSigner) {
        return
      }
      const signer = library.getSigner()
      const wrappedContract = new ethers.Contract(contractAddresses.BotClassicWrapped, botClassicWrappedAbi, signer)
      const tx = await wrappedContract.wrap(id)
      await library.waitForTransaction(tx?.hash)
      getWrappedCount()
      setBotApproved({})
      setBotIds(null)
    },
    [connected, account],
  )

  const clickSelectBot = (id: number) => {
    const index = selectedBotIds.findIndex(i => i === id)
    const _selectedBotIds = [...selectedBotIds]
    if (index === -1) {
      _selectedBotIds.push(id)
    } else {
      _selectedBotIds.splice(index, 1)
    }
    setSelectedBotIds(_selectedBotIds)
  }

  const clearSelectedBots = () => setSelectedBotIds([])

  const wrapMany = async () => {
    if (!connected || !library || !library.getSigner) {
      return
    }
    const signer = library.getSigner()
    const wrappedContract = new ethers.Contract(contractAddresses.BotClassicWrapped, botClassicWrappedAbi, signer)
    const tx = await wrappedContract.wrapMany(selectedBotIds)
    await library.waitForTransaction(tx?.hash)
    clearSelectedBots()
    getWrappedCount()
    setBotApproved({})
    setBotIds(null)
  }
  return (
    <Box className="flex-1 mt-[80px]">
      <BackgroundImage imageSrc="/images/pages/wrap/back.png" hideMobile className="bg-auto md:bg-repeat-x md:bg-cover">
        <Box className="md:p-6">
          <Section>
            <Box className="md:flex-row gap-4 md:gap-6">
              <Box className="gap-4 md:gap-6 flex-1">
                <Text className="text-h2">Classic Bots Wrapping</Text>
                <Text>
                  Wrapped Cryptobots are ERC721 Tokens, each one backed 1:1 by an original Cryptobots 2017.
                  <br />
                  Launched in December 2017, the Cryptobots Classic collection was one of the very first NFT projects
                  and a pioneer p2e game on the market!
                  <br />
                  Today, this collection is considered antique and valued by a big supporting community for its
                  historical value in the NFT space.
                  <br />
                  The collection is limited to 10,000 Bots, the contract ownership has been renounced by the developer.
                  <br />
                  <Link
                    href="https://gitbook.cryptobots.me/cryptobots/classic-cryptobots-wrap"
                    target="_blank"
                    className="mt-2 underline"
                  >
                    Read more about Classic Cryptobots Wrap
                  </Link>
                </Text>
                <Text className="text-h4">Buy Cryptobots on</Text>
                <Box row className="gap-6 -mt-2">
                  {links.map(l => (
                    <Link href={l.url} key={l.url} target="_blank" className="text-base underline">
                      {l.label}
                    </Link>
                  ))}
                </Box>
              </Box>
              <Box className="gap-4 md:gap-6 flex-1">
                <Box className="backdrop-blur-md bg-white/10 p-6 rounded-2xl text-white gap-2">
                  <Text className="text-h4">Connected wallet</Text>
                  <Box row className="justify-between items-center">
                    {account && <Text className="text-h4 opacity-80">{cutAddress(account)}</Text>}
                    {connected ? (
                      <Button onClick={disconnect} type="secondary">
                        Disconnect
                      </Button>
                    ) : (
                      <Box center className="w-full">
                        <Button onClick={isMobile ? connectWalletConnect : connect} type="primary" disabled={loading}>
                          {loading ? "Connecting.." : `Connect ${isMobile ? "Wallet" : "Metamask"}`}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box row className="gap-4 md:gap-6">
                  <Box className="backdrop-blur-md bg-white/10 p-6 rounded-2xl text-white flex-1">
                    <Text className="text-h4">Total Wrapped</Text>
                    <Text className="opacity-80 mt-2 text-base">Bots:</Text>
                    <Text className="text-h3">
                      {dataLoading ? (
                        <Rotating className="animate-spin w-[1.5rem]" />
                      ) : connected ? (
                        wrappedBotsCount
                      ) : (
                        "-"
                      )}
                    </Text>
                  </Box>
                  <Box className="backdrop-blur-md bg-white/10 p-6 rounded-2xl text-white flex-1">
                    <Text className="text-h4">Total Unwrapped</Text>
                    <Text className="opacity-80 mt-2 text-base">Bots:</Text>

                    {!botIds ? (
                      <Button onClick={getBots} loading={botsLoading} size="sm" className="mt-2">
                        Load Bots
                      </Button>
                    ) : (
                      <Text className="text-h3">{connected ? botIds.length : "-"}</Text>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Section>
          {connected && (
            <Section className="-mt-10">
              <Box row className="justify-between">
                <Box>
                  <Text className="text-h3">My Unwrapped Bots</Text>
                  <Text className="text-body">Please select bot to bridge</Text>
                </Box>
                <Box center>
                  {selectedBotIds.length > 0 && (
                    <Box row className="gap-4 md:gap-6">
                      <Button size="sm" onClick={wrapMany}>
                        Wrap selected
                      </Button>
                      <Button size="sm" type="secondary" onClick={clearSelectedBots} className="uppercase">
                        Clear selection
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
              {dataLoading || (botsLoading && <Rotating className="animate-spin w-[2rem]" />)}
              {botsLoadingError && <Text>Error has occured during bots&#39; loading. Try one more time</Text>}
              <Box row className="gap-4 mt-6 flex-wrap">
                {botIds?.map(b => (
                  <BotCard
                    key={b}
                    id={b}
                    blockchainApproved={botApproved[b]}
                    approve={approve}
                    wrap={wrap}
                    onSelect={clickSelectBot}
                    selected={selectedBotIds.some(i => Number(b) === i)}
                  />
                ))}
              </Box>
            </Section>
          )}
        </Box>
      </BackgroundImage>
    </Box>
  )
}

Wrap.requireNetwork = true
Wrap.chain = Mainnet
